import type React from "react";

import { Stack, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import type { PinnedMessages, Message as TMessage } from "api/services/messaging/schemas/messaging";
import { RelativeDatetime } from "shared/atoms/RelativeDatetime";

import { ActivityMessage } from "./ActivityMessage";
import { AppointmentMessage } from "./AppointmentMessage";
import { PatientAssignmentChangeMessage } from "./PatientAssignmentChangeMessage";
import { SystemMessage } from "./SystemMessage";
import { TextMessage } from "./TextMessage";

interface Props {
  message: TMessage;
  pinnedMessages?: PinnedMessages;
  userID?: number;
}

export const Message: React.FC<Props> = ({ message, pinnedMessages, userID }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isOutbound = message?.from?.id === userID;

  const getContent = () => {
    switch (message.type) {
      case "activity": {
        if (!message?.included?.activity) {
          return null;
        }
        return <ActivityMessage message={message} />;
      }
      case "appointment": {
        if (!message?.included?.appointment) {
          return null;
        }
        return <AppointmentMessage appointment={message.included.appointment} />;
      }
      case "insurance_change": {
        return (
          <SystemMessage
            title={t("messages.insurance_change.header")}
            body={message.body ? message.body : t("messages.insurance_change.placeholder")}
          />
        );
      }
      case "patient_assignment_change": {
        if (!message.message_data) {
          return null;
        }
        return <PatientAssignmentChangeMessage message_data={message.message_data} />;
      }
      case "patient_end_treatment": {
        return <SystemMessage body={t("messages.end_treatment")} />;
      }
      case "pt_on_demand_payment_success": {
        return <SystemMessage body={t("messages.system_messages.pt_on_demand_payment_success.title")} />;
      }
      case "pt_on_demand_started": {
        return (
          <SystemMessage
            title={t("messages.system_messages.pt_on_demand_started.title")}
            body={t("messages.system_messages.pt_on_demand_started.body")}
          />
        );
      }
      case "suggested_kickoff_appointment": {
        return (
          <SystemMessage
            title={t("messages.consultation.preferred_time")}
            body={
              message.body
                ? `${t("messages.consultation.patient_statement")} ${message.body}`
                : t("messages.consultation.no_preference")
            }
          />
        );
      }
      case "switched_treatment": {
        // TODO: Improve the translations
        const title = t(`messages.system_messages.${message.type}.title`, {
          date: format(parseISO(message.created_at), "P"),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          previous_lateral_location: t(`patients.lateral_location.${message.previous_lateral_location}`),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          previous_location: t(`patients.pain_location.${message.previous_location}.0`),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          new_lateral_location: t(`patients.lateral_location.${message.new_lateral_location}`),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          new_location: t(`patients.pain_location.${message.new_location}.0`),
        });
        return <SystemMessage title={title} body={t(`messages.system_messages.${message.type}.body`)} />;
      }
      case "text":
        return (
          <TextMessage
            message={{ body: message.body, created_at: message.created_at, id: message.id }}
            pinnedMessages={pinnedMessages}
            isOutbound={isOutbound}
          />
        );
      default:
        return `Unsupported message type, ${message.type}`;
    }
  };

  return (
    <Stack spacing={1} sx={{ wordBreak: "break-word" }}>
      {getContent()}
      {isOutbound && message.read_at && (
        <Typography
          data-testid="read-label"
          variant="label"
          sx={{ alignSelf: "flex-end" }}
          color={theme.colors.redesign.db60}
        >
          {t("messages.read")} <RelativeDatetime dateString={message.read_at} />
        </Typography>
      )}
    </Stack>
  );
};
